import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { acceptInvitation } from "../../api/members";
import { fetchProject, fetchProjects, type Project } from "../../api/projects";
import {
  createThreadV4NotebookEntry,
  createThreadV4View,
  fetchThreadV4Notebook,
  fetchThreadV4Views,
  updateThreadV4View,
  type ThreadV4NotebookEntry,
  type ThreadV4ViewSpec,
} from "../../api/threadV4Views";
import { PlotDataProvider } from "../../context/PlotDataContext";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import { ViewStateProvider } from "../../context/ViewStateContext";
import { authClient } from "../../lib/auth";
import { checkAuthStatus, getFlow360AccessToken, getFlow360UserEmail } from "../../flow360/api";
import { useV3Data } from "../thread-v3/useV3Data";
import { rowKey } from "../thread-v4/ThreadV4Widgets";
import { isUuidLike } from "../thread-v4/viewSpecs";
import { AdbExplorerSurface } from "./ThreadV5AdbExplorer";
import { Flow360ImportSurface } from "./ThreadV5Flow360Import";
import {
  ArtifactsSurface,
  BranchingSurface,
  type DashboardSaveRequest,
  DashboardEditorSurface,
  DashboardViewSurface,
  DataSurface,
  FocusLayer,
  type Flow360ConnectorSummary,
  HomeSurface,
  LineageFullSurface,
  NotebookDrawer,
  ProjectChatDrawer,
  ProjectMembersSurface,
  ProjectsSurface,
  ProvenanceSurface,
  ThreadV5NavRail,
  ThreadV5Shell,
  WorkbenchSurface,
} from "./ThreadV5Surfaces";
import { useProjectChat, useThreadPresence } from "./useV5Realtime";
import "./thread-v5.css";

// FXC-8630: "lineage" is the new first-class full-surface lineage view.
export type V5View = "projects" | "workbench" | "home" | "data" | "artifacts" | "provenance" | "branching" | "members" | "dashboard-edit" | "tailor" | "filament-model" | "flow360-import" | "lineage";
export type V5Focus = "artifact" | "flowfield" | "dag" | "geometry" | "notebook" | null;
export type V5FlowfieldPair = "artifact" | "geometry" | "dag" | "table" | "correlogram" | null;
type V5Drawer = "spool" | "chat" | null;
export type V5TableMode = "table" | "correlogram";
export type V5PlotSize = "large" | null;

const V5_VIEWS = new Set<V5View>(["projects", "workbench", "home", "data", "artifacts", "provenance", "branching", "members", "dashboard-edit", "tailor", "filament-model", "flow360-import", "lineage"]);
const V5_FOCI = new Set<Exclude<V5Focus, null>>(["artifact", "flowfield", "dag", "geometry", "notebook"]);
const V5_DRAWERS = new Set<Exclude<V5Drawer, null>>(["spool", "chat"]);
const CLEARED_RUN_PARAM = "none";

function requestedProjectId(routeProjectId: string | undefined, searchParams: URLSearchParams): string | undefined {
  return routeProjectId ?? searchParams.get("project") ?? undefined;
}

function viewFromParam(value: string | null, fallback: V5View): V5View {
  if (value === "flow360-import") return "filament-model";
  return value && V5_VIEWS.has(value as V5View) ? (value as V5View) : fallback;
}

function focusFromParam(value: string | null): V5Focus {
  return value && V5_FOCI.has(value as Exclude<V5Focus, null>) ? (value as V5Focus) : null;
}

function focusFromSearchParams(searchParams: URLSearchParams): V5Focus {
  const panel = searchParams.get("panel");
  if (panel === "viewer") return "flowfield";
  if (panel === "dag") return "dag";
  if (panel === "geometry") return "geometry";
  return focusFromParam(searchParams.get("focus"));
}

function drawerFromSearchParams(searchParams: URLSearchParams): V5Drawer {
  const drawer = searchParams.get("drawer");
  if (drawer === "notebook") return "spool";
  if (drawer && V5_DRAWERS.has(drawer as Exclude<V5Drawer, null>)) return drawer as Exclude<V5Drawer, null>;
  return searchParams.get("focus") === "notebook" ? "spool" : null;
}

function runFromSearchParams(searchParams: URLSearchParams): string | null {
  const run = searchParams.get("run");
  return run && run !== CLEARED_RUN_PARAM ? run : null;
}

function plotSizeFromSearchParams(searchParams: URLSearchParams): V5PlotSize {
  return searchParams.get("plot") === "large" ? "large" : null;
}

function flowfieldPairFromSearchParams(
  searchParams: URLSearchParams,
  activeView: V5View,
  tableMode: V5TableMode,
  focusedAssetId: string | null,
): V5FlowfieldPair {
  if (searchParams.get("panel") !== "viewer") return null;
  const previousFocus = focusFromParam(searchParams.get("focus"));
  if (previousFocus === "artifact" && focusedAssetId) return "artifact";
  if (previousFocus === "geometry" || previousFocus === "dag") return previousFocus;
  if (focusedAssetId) return "artifact";
  if (activeView === "workbench" || activeView === "data") {
    return tableMode === "correlogram" ? "correlogram" : "table";
  }
  return null;
}

function initialsFromIdentity(identity: string | null | undefined): string {
  const trimmed = (identity ?? "").trim();
  if (!trimmed) return "UN";
  const label = trimmed.includes("@") ? trimmed.split("@")[0] ?? trimmed : trimmed;
  const parts = label.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1
    ? `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`
    : label.slice(0, 2);
  return (letters || "UN").toUpperCase();
}

function storedFlow360Email(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem("flow360_user_email");
  } catch {
    return null;
  }
}

function storedFlow360Flag(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function localStorageKey(projectId: string, branch: string, kind: "views" | "notebook"): string {
  return `thread:v5:local:${projectId}:${branch}:${kind}`;
}

function readLocalNotebook(projectId: string, branch: string): ThreadV4NotebookEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localStorageKey(projectId, branch, "notebook")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalNotebook(projectId: string, branch: string, entries: ThreadV4NotebookEntry[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localStorageKey(projectId, branch, "notebook"), JSON.stringify(entries));
}

function readLocalViews(projectId: string, branch: string): ThreadV4ViewSpec[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localStorageKey(projectId, branch, "views")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalViews(projectId: string, branch: string, views: ThreadV4ViewSpec[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localStorageKey(projectId, branch, "views"), JSON.stringify(views));
}

function makeLocalNotebookEntry(projectId: string, branch: string, body: string, selectedRunId: string | null): ThreadV4NotebookEntry {
  return {
    id: `v5-local-note-${Date.now().toString(36)}`,
    project_id: projectId,
    view_id: null,
    branch,
    entry_type: "note",
    author_id: "local-user",
    author_label: "You",
    owner_user_id: "local-user",
    visibility: "work_shared",
    source_type: "manual",
    source_uri: null,
    source_external_id: null,
    source_created_at: null,
    captured_at: new Date().toISOString(),
    body,
    body_format: "markdown",
    anchors: selectedRunId ? { run_id: selectedRunId } : { project_id: projectId },
    source_metadata: {},
    metadata: { surface: "thread-v5" },
    created_at: new Date().toISOString(),
    updated_at: null,
  };
}

function makeDashboardView(
  projectId: string,
  branch: string,
  name: string,
  selectedRunId: string | null,
  overrides: Partial<Pick<ThreadV4ViewSpec, "layout_json" | "widget_state_json" | "binding_json" | "metadata">> = {},
): ThreadV4ViewSpec {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "v5-board";
  const suffix = Date.now().toString(36).slice(-5);
  return {
    id: `v5-local-view-${Date.now().toString(36)}`,
    project_id: projectId,
    branch,
    name,
    slug: `${slug}-${suffix}`,
    category: "custom",
    visibility: "project",
    owner_id: "local-user",
    layout_json: overrides.layout_json ?? {
      schema_version: "thread.view.layout.v1",
      mode: "dashboard",
      cols: 12,
      row_height: 96,
      widgets: [
        { id: "runs", kind: "table", title: "Simulation runs", position: { x: 0, y: 0, w: 7, h: 4 } },
        { id: "artifacts", kind: "asset-rail", title: "Artifact shelf", position: { x: 7, y: 0, w: 3, h: 4, dock: "right" } },
        { id: "dag", kind: "dag", title: "DAG minimap", position: { x: 0, y: 4, w: 3, h: 2 } },
        { id: "flowfield", kind: "flowfield", title: "Flowfield", position: { x: 10, y: 0, w: 2, h: 4, dock: "right" } },
        { id: "notebook", kind: "notebook", title: "Spool", position: { x: 3, y: 4, w: 4, h: 2, dock: "bottom" } },
      ],
    },
    widget_state_json: overrides.widget_state_json ?? {},
    binding_json: overrides.binding_json ?? (selectedRunId ? { selected_run_id: selectedRunId } : {}),
    metadata: { source: "thread-v5", design: "Thread V5", ...(overrides.metadata ?? {}) },
    thumbnail_asset_id: null,
    is_default: false,
    created_at: new Date().toISOString(),
    updated_at: null,
  };
}

export default function ThreadV5WorkbenchPage() {
  const { projectId: routeProjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeProject = requestedProjectId(routeProjectId, searchParams);
  const requestedBranch = searchParams.get("branch");
  const inviteParam = searchParams.get("invite");
  const dataSearchParams = new URLSearchParams(searchParams);
  for (const key of ["view", "focus", "run", "artifact", "dashboard", "panel", "configTab", "plot", "nodes", "invite", "drawer"]) dataSearchParams.delete(key);
  const v3State = useV3Data(routeProject, requestedBranch, dataSearchParams.toString());
  const data = v3State.data;
  const projectId = routeProject ?? data.project?.id ?? "local-thread-project";
  const branch = data.branch || requestedBranch || "main";
  const persistedProject = isUuidLike(projectId);
  const [projectSummary, setProjectSummary] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);
  const fixtureName = searchParams.get("fixture");
  const projectName = projectSummary?.name ?? data.project?.name ?? (fixtureName ? `${fixtureName} fixture` : "Local Thread data");

  const defaultView = routeProject ? "workbench" : "projects";
  const [activeView, setActiveView] = useState<V5View>(() => viewFromParam(searchParams.get("view"), defaultView));
  const [focus, setFocus] = useState<V5Focus>(() => focusFromSearchParams(searchParams));
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => runFromSearchParams(searchParams));
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(() => searchParams.get("nodes"));
  const [focusedAssetId, setFocusedAssetId] = useState<string | null>(() => searchParams.get("artifact"));
  const [tableMode, setTableMode] = useState<V5TableMode>(() => (searchParams.get("mode") === "correlogram" ? "correlogram" : "table"));
  const [plotSize, setPlotSize] = useState<V5PlotSize>(() => plotSizeFromSearchParams(searchParams));
  const [notebookEntries, setNotebookEntries] = useState<ThreadV4NotebookEntry[]>([]);
  const [notebookDraft, setNotebookDraft] = useState("");
  const [chatDraft, setChatDraft] = useState("");
  const [views, setViews] = useState<ThreadV4ViewSpec[]>([]);
  const [activeDashboardName, setActiveDashboardName] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState("UN");
  const [flow360ConnectorRefreshKey, setFlow360ConnectorRefreshKey] = useState(0);
  const [flow360AuthStatus, setFlow360AuthStatus] = useState<Awaited<ReturnType<typeof checkAuthStatus>> | null>(null);
  const [acceptingProjectId, setAcceptingProjectId] = useState<string | null>(null);
  const [acceptInviteError, setAcceptInviteError] = useState<string | null>(null);
  const inviteAcceptAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const applyIdentity = (session: { user?: { email?: string; id?: string } } | null) => {
      if (cancelled) return;
      setUserInitials(initialsFromIdentity(session?.user?.email ?? storedFlow360Email() ?? session?.user?.id));
    };

    authClient.auth.getSession().then(({ data: { session } }) => applyIdentity(session));
    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((_event, session) => applyIdentity(session));
    const refreshFromFlow360 = () => applyIdentity(null);
    window.addEventListener("flow360-auth-ready", refreshFromFlow360);
    window.addEventListener("storage", refreshFromFlow360);
    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.removeEventListener("flow360-auth-ready", refreshFromFlow360);
      window.removeEventListener("storage", refreshFromFlow360);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    checkAuthStatus()
      .then((status) => {
        if (!cancelled) setFlow360AuthStatus(status);
      })
      .catch(() => {
        if (!cancelled) setFlow360AuthStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [flow360ConnectorRefreshKey]);

  useEffect(() => {
    const refreshFlow360Connector = () => setFlow360ConnectorRefreshKey((key) => key + 1);
    window.addEventListener("flow360-auth-ready", refreshFlow360Connector);
    window.addEventListener("storage", refreshFlow360Connector);
    return () => {
      window.removeEventListener("flow360-auth-ready", refreshFlow360Connector);
      window.removeEventListener("storage", refreshFlow360Connector);
    };
  }, []);

  useEffect(() => {
    setActiveView(viewFromParam(searchParams.get("view"), routeProject ? "workbench" : "projects"));
    setFocus(focusFromSearchParams(searchParams));
    setSelectedRunId(runFromSearchParams(searchParams));
    setSelectedNodeId(searchParams.get("nodes"));
    setFocusedAssetId(searchParams.get("artifact"));
    setTableMode(searchParams.get("mode") === "correlogram" ? "correlogram" : "table");
    setPlotSize(plotSizeFromSearchParams(searchParams));
    setActiveDashboardName(searchParams.get("dashboard"));
  }, [routeProject, searchParams]);

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    fetchProjects()
      .then((items) => {
        if (cancelled) return;
        setProjects(items);
        setProjectsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setProjects([]);
        setProjectsError(error instanceof Error ? error.message : "Unable to load projects");
        setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectsRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    if (!persistedProject) {
      setProjectSummary(null);
      return;
    }
    fetchProject(projectId)
      .then((project) => {
        if (!cancelled) setProjectSummary(project);
      })
      .catch(() => {
        if (!cancelled) setProjectSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [persistedProject, projectId]);

  useEffect(() => {
    if (!routeProject || inviteParam !== "1") return;
    const inviteKey = `${routeProject}:1`;
    if (inviteAcceptAttemptedRef.current === inviteKey) return;
    inviteAcceptAttemptedRef.current = inviteKey;

    let cancelled = false;
    acceptInvitation(routeProject)
      .catch(() => {
        // Already-accepted invites can reject because there is no pending row.
        // The normal project load still determines whether the signed-in user has access.
      })
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete("invite");
        setSearchParams(next, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [inviteParam, routeProject, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!persistedProject) {
      setViews(readLocalViews(projectId, branch));
      return;
    }
    fetchThreadV4Views(projectId, branch)
      .then((response) => {
        if (!cancelled) setViews(response.views.filter((view) => view.category !== "standard"));
      })
      .catch(() => {
        if (!cancelled) setViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, persistedProject, projectId]);

  useEffect(() => {
    let cancelled = false;
    if (!persistedProject) {
      setNotebookEntries(readLocalNotebook(projectId, branch));
      return;
    }
    fetchThreadV4Notebook(projectId, branch)
      .then((response) => {
        if (!cancelled) setNotebookEntries(response.entries);
      })
      .catch(() => {
        if (!cancelled) setNotebookEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, persistedProject, projectId]);

  const updateRoute = useCallback((patch: { view?: V5View; focus?: V5Focus; drawer?: V5Drawer; run?: string | null; clearRun?: boolean; artifact?: string | null; dashboard?: string | null; mode?: V5TableMode | null; nodes?: string | null; panel?: string | null; configTab?: string | null; plot?: V5PlotSize }, options: { replace?: boolean } = {}) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (patch.view !== undefined) {
        if (patch.view === "workbench") params.delete("view");
        else params.set("view", patch.view);
      }
      if (patch.focus !== undefined) {
        if (patch.focus) params.set("focus", patch.focus);
        else params.delete("focus");
      }
      if (patch.drawer !== undefined) {
        if (patch.drawer) params.set("drawer", patch.drawer);
        else params.delete("drawer");
        if (patch.drawer) params.delete("focus");
      }
      if (patch.run !== undefined) {
        if (patch.clearRun) params.set("run", CLEARED_RUN_PARAM);
        else if (patch.run) params.set("run", patch.run);
        else params.delete("run");
      } else if (patch.clearRun) {
        params.set("run", CLEARED_RUN_PARAM);
      }
      if (patch.artifact !== undefined) {
        if (patch.artifact) params.set("artifact", patch.artifact);
        else params.delete("artifact");
      }
      if (patch.dashboard !== undefined) {
        if (patch.dashboard) params.set("dashboard", patch.dashboard);
        else params.delete("dashboard");
      }
      if (patch.mode !== undefined) {
        if (patch.mode && patch.mode !== "table") params.set("mode", patch.mode);
        else params.delete("mode");
      }
      if (patch.nodes !== undefined) {
        if (patch.nodes) params.set("nodes", patch.nodes);
        else params.delete("nodes");
      }
      if (patch.panel !== undefined) {
        if (patch.panel) params.set("panel", patch.panel);
        else params.delete("panel");
      }
      if (patch.configTab !== undefined) {
        if (patch.configTab) params.set("configTab", patch.configTab);
        else params.delete("configTab");
      }
      if (patch.plot !== undefined) {
        if (patch.plot) params.set("plot", patch.plot);
        else params.delete("plot");
      }
      return params;
    }, { replace: options.replace ?? false });
  }, [setSearchParams]);

  useEffect(() => {
    if (selectedRunId || searchParams.has("run") || !routeProject || activeView === "projects" || !data.rows[0]) return;
    if ((data.totalRowCount || data.rows.length) > 0) {
      const firstRow = data.rows[0];
      updateRoute({ run: rowKey(firstRow), nodes: firstRow.rowNodeId ?? null }, { replace: true });
    }
  }, [activeView, data.rows, data.totalRowCount, routeProject, searchParams, selectedRunId, updateRoute]);

  const openView = useCallback((view: V5View) => {
    updateRoute({ view, focus: null, drawer: null, dashboard: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const openProjects = useCallback(() => {
    updateRoute({ view: "projects", focus: null, drawer: null, run: null, artifact: null, dashboard: null, nodes: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const openMembers = useCallback(() => {
    updateRoute({ view: "members", focus: null, drawer: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const openProject = useCallback((project: Project) => {
    const params = new URLSearchParams(location.search);
    for (const key of ["view", "focus", "drawer", "run", "artifact", "dashboard", "mode", "project", "fixture", "nodes", "invite"]) {
      params.delete(key);
    }
    if (!params.has("source")) params.set("source", "run-table");
    if (!params.has("graph")) params.set("graph", "1");
    const query = params.toString();
    navigate(`/v5/${project.id}${query ? `?${query}` : ""}`);
  }, [location.search, navigate]);

  const acceptListedProjectInvite = useCallback(async (project: Project) => {
    setAcceptingProjectId(project.id);
    setAcceptInviteError(null);
    try {
      await acceptInvitation(project.id);
      setProjectsRefreshKey((key) => key + 1);
      openProject(project);
    } catch (error) {
      setAcceptInviteError(error instanceof Error ? error.message : "Unable to accept invitation");
    } finally {
      setAcceptingProjectId((current) => current === project.id ? null : current);
    }
  }, [openProject]);

  const selectRun = useCallback((runId: string, nodeId?: string | null) => {
    updateRoute({ view: activeView === "home" ? "workbench" : activeView, run: runId, nodes: nodeId ?? null, focus: null, panel: null, configTab: null, plot: null });
  }, [activeView, updateRoute]);

  const selectPlotPoint = useCallback((runId: string | null, nodeId: string | null) => {
    updateRoute({ run: runId ?? selectedRunId, nodes: nodeId ?? null });
  }, [selectedRunId, updateRoute]);

  const openArtifact = useCallback((assetId: string) => {
    const asset = data.assets.find((item) => item.id === assetId);
    const rowId = asset?.refs.rows?.[0] ?? selectedRunId;
    if (asset?.type === "uvf") {
      updateRoute({
        view: "workbench",
        focus: "flowfield",
        artifact: assetId,
        run: rowId ?? null,
        nodes: asset.provenanceNodeId ?? asset.id,
        panel: null,
        configTab: null,
        plot: null,
      });
      return;
    }
    updateRoute({ view: "workbench", focus: "artifact", artifact: assetId, run: rowId ?? null, panel: null, configTab: null, plot: null });
  }, [data.assets, selectedRunId, updateRoute]);

  const openFocus = useCallback((nextFocus: V5Focus) => {
    if (nextFocus === "notebook") {
      updateRoute({ drawer: drawerFromSearchParams(searchParams) === "spool" ? null : "spool", panel: null, configTab: null, plot: null });
      return;
    }
    updateRoute({ view: "workbench", focus: nextFocus, panel: null, configTab: null, plot: null });
  }, [searchParams, updateRoute]);

  const setPlotLarge = useCallback((large: boolean) => {
    updateRoute({ plot: large ? "large" : null });
  }, [updateRoute]);

  const openDashboard = useCallback((view: ThreadV4ViewSpec) => {
    const selected = typeof view.binding_json.selected_run_id === "string" ? view.binding_json.selected_run_id : null;
    const mode = view.layout_json.widgets.some((widget) => widget.kind === "plot") ? "correlogram" : "table";
    updateRoute({ view: "workbench", focus: null, run: selected, nodes: null, dashboard: view.id, mode });
  }, [updateRoute]);

  const saveDashboard = useCallback((request: DashboardSaveRequest) => {
    const localView = makeDashboardView(projectId, branch, request.name, selectedRunId, {
      layout_json: request.layout_json,
      widget_state_json: request.widget_state_json,
      binding_json: request.binding_json,
      metadata: request.metadata,
    });
    const updateExisting = Boolean(request.updateExisting && request.sourceViewId);
    if (!persistedProject) {
      const next = updateExisting
        ? views.map((view) => view.id === request.sourceViewId ? { ...view, ...localView, id: view.id, slug: view.slug, created_at: view.created_at, updated_at: new Date().toISOString() } : view)
        : [localView, ...views];
      setViews(next);
      writeLocalViews(projectId, branch, next);
      openDashboard(updateExisting ? next.find((view) => view.id === request.sourceViewId) ?? localView : localView);
      return;
    }
    if (updateExisting && request.sourceViewId && isUuidLike(request.sourceViewId)) {
      updateThreadV4View(request.sourceViewId, {
        name: request.name,
        layout_json: request.layout_json,
        widget_state_json: request.widget_state_json ?? {},
        binding_json: request.binding_json ?? {},
        metadata: request.metadata ?? {},
      })
        .then((updated) => {
          setViews((current) => current.map((view) => view.id === updated.id ? updated : view));
          openDashboard(updated);
        })
        .catch(() => {
          const next = [localView, ...views];
          setViews(next);
          openDashboard(localView);
        });
      return;
    }
    createThreadV4View({
      project_id: projectId,
      branch,
      name: request.name,
      slug: localView.slug,
      category: "custom",
      visibility: "project",
      layout_json: request.layout_json,
      binding_json: request.binding_json ?? localView.binding_json,
      widget_state_json: request.widget_state_json ?? {},
      metadata: localView.metadata,
    })
      .then((created) => {
        setViews((current) => [created, ...current.filter((view) => view.id !== created.id)]);
        openDashboard(created);
      })
      .catch(() => {
        const next = [...views, localView];
        setViews(next);
        openDashboard(localView);
      });
  }, [branch, openDashboard, persistedProject, projectId, selectedRunId, views]);

  const addNotebookEntry = useCallback(() => {
    const body = notebookDraft.trim();
    if (!body) return;
    const localEntry = makeLocalNotebookEntry(projectId, branch, body, selectedRunId);
    setNotebookDraft("");
    if (!persistedProject) {
      const next = [localEntry, ...notebookEntries];
      setNotebookEntries(next);
      writeLocalNotebook(projectId, branch, next);
      return;
    }
    createThreadV4NotebookEntry({
      project_id: projectId,
      branch,
      entry_type: "note",
      author_label: "You",
      visibility: "work_shared",
      source_type: "manual",
      body,
      body_format: "markdown",
      anchors: selectedRunId ? { run_id: selectedRunId } : { project_id: projectId },
      metadata: { surface: "thread-v5" },
    })
      .then((entry) => setNotebookEntries((current) => [entry, ...current]))
      .catch(() => setNotebookEntries((current) => [localEntry, ...current]));
  }, [branch, notebookDraft, notebookEntries, persistedProject, projectId, selectedRunId]);

  const openFlow360 = useCallback(() => {
    const params = new URLSearchParams();
    if (persistedProject) params.set("thread_project_id", projectId);
    params.set("return_to", `${location.pathname}${location.search}`);
    navigate(`/flow360?${params.toString()}`);
  }, [location.pathname, location.search, navigate, persistedProject, projectId]);

  const activeDashboardView = useMemo(() => {
    if (!activeDashboardName) return null;
    return views.find((view) => view.id === activeDashboardName || view.slug === activeDashboardName || view.name === activeDashboardName) ?? null;
  }, [activeDashboardName, views]);
  const activeDashboardDisplayName = activeDashboardView?.name ?? activeDashboardName;
  const routeDrawer = drawerFromSearchParams(searchParams);
  const notebookOpen = routeDrawer === "spool";
  const realtimeEnabled = Boolean(persistedProject && activeView !== "projects");
  const chatOpen = routeDrawer === "chat" && realtimeEnabled;
  const chatRealtimeEnabled = chatOpen;
  const flowfieldPair = useMemo(
    () => flowfieldPairFromSearchParams(searchParams, activeView, tableMode, focusedAssetId),
    [activeView, focusedAssetId, searchParams, tableMode],
  );
  const presenceViewId = useMemo(() => {
    const dashboard = activeDashboardView?.id ?? activeDashboardName ?? "default";
    return `v5:${activeView}:${dashboard}:${selectedRunId ?? "project"}`;
  }, [activeDashboardName, activeDashboardView?.id, activeView, selectedRunId]);
  const presenceSelection = useMemo<Record<string, unknown>>(() => ({
    view: activeView,
    branch,
    selected_run_id: selectedRunId,
    selected_node_id: selectedNodeId,
    focused_asset_id: focusedAssetId,
    dashboard: activeDashboardDisplayName,
  }), [activeDashboardDisplayName, activeView, branch, focusedAssetId, selectedNodeId, selectedRunId]);
  const presence = useThreadPresence({
    projectId,
    enabled: realtimeEnabled,
    viewId: presenceViewId,
    selection: presenceSelection,
  });
  const projectChat = useProjectChat({
    projectId,
    enabled: chatRealtimeEnabled,
    open: chatOpen,
  });
  const flow360Connector = useMemo<Flow360ConnectorSummary>(() => {
    const browserSession = typeof window !== "undefined" && Boolean(getFlow360AccessToken());
    const storedKey = Boolean(flow360AuthStatus?.has_stored_key) || storedFlow360Flag("flow360_api_key_stored");
    const connectedToFlow360 = Boolean(flow360AuthStatus?.authenticated || browserSession || storedKey);
    const email = getFlow360UserEmail() ?? flow360AuthStatus?.user?.email ?? null;
    const statusLabel = storedKey
      ? "Thread credential"
      : browserSession
        ? "browser session"
        : connectedToFlow360
          ? "connected"
          : "connect";
    const detail = storedKey
      ? "Flow360 auth is minted into the Thread profile and available across Thread surfaces."
      : browserSession
        ? "This browser tab has a Flow360 session; Thread can mint it into the shared connector credential."
        : "Connect Flow360 once so Thread can import, refresh, fork, sweep, and run Filament/Tailor workflows.";
    return {
      connected: connectedToFlow360,
      browserSession,
      storedKey,
      email,
      statusLabel,
      detail,
    };
  }, [flow360AuthStatus, flow360ConnectorRefreshKey]);
  const toggleNotebook = useCallback(() => {
    updateRoute({ drawer: notebookOpen ? null : "spool" });
  }, [notebookOpen, updateRoute]);
  const toggleChat = useCallback(() => {
    updateRoute({ drawer: chatOpen ? null : "chat" });
  }, [chatOpen, updateRoute]);
  const sendProjectChat = useCallback(async () => {
    const sent = await projectChat.sendMessage(chatDraft);
    if (sent) setChatDraft("");
  }, [chatDraft, projectChat]);

  const surfaceProps = useMemo(() => ({
    data,
    dataLoading: v3State.status === "loading",
    projectId,
    projectName,
    branch,
    persistedProject,
    selectedRunId,
    selectedNodeId,
    focusedAssetId,
    tableMode,
    activeDashboardName: activeDashboardDisplayName,
    currentView: activeDashboardView,
    views,
    notebookEntries,
    onTableModeChange: (mode: V5TableMode) => {
      setTableMode(mode);
      updateRoute({ mode });
    },
    onSelectRun: selectRun,
    onOpenArtifact: openArtifact,
    onOpenFocus: openFocus,
    onOpenFlow360: openFlow360,
    onOpenView: openView,
    onOpenDashboard: openDashboard,
    onSaveDashboard: saveDashboard,
  }), [activeDashboardDisplayName, activeDashboardView, branch, data, focusedAssetId, notebookEntries, openArtifact, openDashboard, openFlow360, openFocus, openView, persistedProject, projectId, projectName, saveDashboard, selectRun, selectedNodeId, selectedRunId, tableMode, updateRoute, v3State.status, views]);

  const surface = (() => {
    switch (activeView) {
      case "projects":
        return (
          <ProjectsSurface
            projects={projects}
            loading={projectsLoading}
            error={projectsError}
            currentProjectId={routeProject ?? null}
            onOpenProject={openProject}
            onAcceptInvitation={acceptListedProjectInvite}
            onRefresh={() => setProjectsRefreshKey((key) => key + 1)}
            onOpenFlow360={openFlow360}
            acceptingProjectId={acceptingProjectId}
            acceptError={acceptInviteError}
          />
        );
      case "home":
        return <HomeSurface {...surfaceProps} />;
      case "data":
        return <DataSurface {...surfaceProps} />;
      case "artifacts":
        return <ArtifactsSurface {...surfaceProps} />;
      case "provenance":
        return <ProvenanceSurface {...surfaceProps} />;
      // FXC-8630: full-surface lineage view (Warp as first-class V5 nav entry).
      case "lineage":
        return <LineageFullSurface {...surfaceProps} />;
      case "branching":
        return <BranchingSurface {...surfaceProps} />;
      case "tailor":
        return <AdbExplorerSurface {...surfaceProps} />;
      case "filament-model":
      case "flow360-import":
        return <Flow360ImportSurface {...surfaceProps} />;
      case "members":
        return <ProjectMembersSurface {...surfaceProps} />;
      case "dashboard-edit":
        return <DashboardEditorSurface {...surfaceProps} />;
      case "workbench":
      default:
        if (activeDashboardView) return <DashboardViewSurface {...surfaceProps} />;
        return <WorkbenchSurface {...surfaceProps} />;
    }
  })();

  const connected = v3State.status === "ready" && (data.rows.length > 0 || data.errors.length === 0);

  return (
    <ProvenanceProvider projectId={projectId} branch={branch}>
      <ViewStateProvider>
        <PlotDataProvider>
          <div className="v5-app">
            <ThreadV5Shell
              projectName={activeView === "projects" && !routeProject ? "Projects" : projectName}
              branch={activeView === "projects" && !routeProject ? null : branch}
              connected={connected}
              selectedRunId={activeView === "projects" && !routeProject ? null : selectedRunId}
              activeDashboardName={activeDashboardDisplayName}
              notebookOpen={notebookOpen}
              userInitials={userInitials}
              flow360Connector={flow360Connector}
              presenceStatus={presence.status}
              presenceUsers={presence.peers}
              chatOpen={chatOpen}
              chatUnreadCount={projectChat.unreadCount}
              onClearSelection={() => updateRoute({ run: null, clearRun: true, nodes: null, artifact: null, focus: null, panel: null, configTab: null, plot: null })}
              onOpenBranching={() => openView("branching")}
              onOpenFlow360={openFlow360}
              onOpenNotebook={toggleNotebook}
              onOpenChat={realtimeEnabled ? toggleChat : undefined}
              onOpenMembers={openMembers}
              onOpenProjects={openProjects}
              onCreateDashboard={() => openView("dashboard-edit")}
            />
            <ThreadV5NavRail activeView={activeView} dashboards={views} onOpenView={openView} onOpenDashboard={openDashboard} />
            <div className={`v5-content ${notebookOpen || chatOpen ? "has-spool" : ""}`}>
              {surface}
              {notebookOpen ? (
                <NotebookDrawer
                  selectedRunId={selectedRunId}
                  entries={notebookEntries}
                  draft={notebookDraft}
                  onDraftChange={setNotebookDraft}
                  onAdd={addNotebookEntry}
                  onClose={() => updateRoute({ drawer: null })}
                />
              ) : null}
              {chatOpen ? (
                <ProjectChatDrawer
                  projectName={projectName}
                  status={projectChat.status}
                  streamStatus={projectChat.streamStatus}
                  messages={projectChat.messages}
                  draft={chatDraft}
                  sending={projectChat.sending}
                  typingUsers={projectChat.typingUsers}
                  error={projectChat.error}
                  onDraftChange={setChatDraft}
                  onTyping={projectChat.notifyTyping}
                  onSend={sendProjectChat}
                  onClose={() => updateRoute({ drawer: null })}
                />
              ) : null}
              <FocusLayer
                focus={focus}
                flowfieldPair={flowfieldPair}
                data={data}
                projectId={projectId}
                persistedProject={persistedProject}
                selectedRunId={selectedRunId}
                selectedNodeId={selectedNodeId}
                focusedAssetId={focusedAssetId}
                tableMode={tableMode}
                notebookEntries={notebookEntries}
                plotLarge={plotSize === "large"}
                onPlotLargeChange={setPlotLarge}
                onClose={() => updateRoute({ focus: null, artifact: null, panel: null, configTab: null, plot: null })}
                onSelectRun={selectRun}
                onSelectPlotPoint={selectPlotPoint}
                onFullProvenance={() => updateRoute({ view: "provenance", focus: null, panel: null, plot: null })}
              />
            </div>
          </div>
        </PlotDataProvider>
      </ViewStateProvider>
    </ProvenanceProvider>
  );
}
